import React, { useMemo, useEffect, useState, useCallback, useRef, useImperativeHandle } from 'react';
import { Form, notification, Modal, message, Button, Spin } from 'antd';
import { FloppyDisk, X } from '@phosphor-icons/react';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import designServices from '../../../../services/oneButtonServices/designServices';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/BatteryDetailsModal.css';
import './oneButton-css/OneButtonFormControls.css';

import { opportunityHasSolarFinancePartner } from './ProductionsCard';
import {
    DEFAULT_BATTERY_MANUFACTURER,
    DEFAULT_BATTERY_MODEL_NAME,
    DEFAULT_BATTERY_QUANTITY,
    pickBatteryManufacturerValue,
    pickBatteryModelValue,
    pickBatteryQuantityValue,
} from '../../../../helpers/productDesignFallbacks';
import {
    type ProductDesignsEmbeddedBridgeProps,
    useProductDesignsEmbeddedFooter,
} from './productDesignsEmbeddedFooter';
const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;

/** Trinity `UpdateLoanStatus` field — same as `ProductionsCard` force-sync confirm. */
export const UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD = 'SystemDesign';

interface BatteryDetailsModalProps extends ProductDesignsEmbeddedBridgeProps {
    open: boolean;
    onClose: () => void;
    batteryData: { manufacturer: string; name: string; quantity: string | number; rawName?: string } | null;
    /** Render body only (no DashboardModal shell) for ProductDesignsModal tabs */
    embedded?: boolean;
    /** Render the existing Save/Cancel footer in the body instead of a parent footer portal. */
    embedFooterInline?: boolean;
    ref?:any
}

const BatteryDetailsModal: React.FC<BatteryDetailsModalProps> = ({
    open,
    onClose,
    batteryData,
    embedded = false,
    embeddedActive,
    onEmbeddedFooterChange,
    embedFooterInline = false,
    ref
}) => {
    const {
        equipmentMasterData,
        systemDesign,
        setSystemDesign,
        opportunityDetails,
        productType: globalProductType,
        theme,
        batteryModelData,
        setBatteryModelData,
        onebuttonCapabilities,
        setQuotesRefreshTrigger,
        setPriceQuoteDetails
    } = useOneButtonStore();
    const [form] = Form.useForm();
    // Hook for premium notifications
    const [noticeAPI, noticeContext] = notification.useNotification({ maxCount: 4, stack: false });
    
    const [fetchedOpts, setFetchedOpts] = useState<{ manufacturers: any[] | null, models: any[] | null, quantities: any[] | null }>({ manufacturers: null, models: null, quantities: null });
    const [batteryExtraInfo, setBatteryExtraInfo] = useState<{ BatteryCoupling: string | null; BatteryEquipmentId: string | null; BatteryInverterEquipmentId: string | null; BatteryInverterQuantity: string | null } | null>(null);
    const [loading, setLoading] = useState({ initial: false, secondary: false, saving: false });

    const selectedMfg = Form.useWatch('manufacturer', form);
    const selectedQty = Form.useWatch('quantity', form);
    const isTesla = selectedMfg?.toLowerCase() === 'tesla';
    const changeOrderDetails = useOneButtonStore((s) => s.changeOrderDetails);

    const [batteryChangeOrderConfirmOpen, setBatteryChangeOrderConfirmOpen] = useState(false);
    const pendingBatteryFormValsRef = useRef<{ manufacturer: string; name: string; quantity: string | number } | null>(null);
    const awaitingBatteryCoConfirmRef = useRef(false);

    const setPriceQuoteOverrides = useOneButtonStore((s) => s.setPriceQuoteOverrides);

    /** Angular `battery-details`: `authService.getCapability('UI_Design_Info_Access') == 'View'` → disable all form controls */
    const designInfoAccessView = getOneButtonCapabilityValue(onebuttonCapabilities, 'UI_Design_Info_Access') === 'View';

    // Provides mapping for Excel calculation inputs from global state and opportunity details
    const getExcelContext = useCallback(() => {
        const root = opportunityDetails || systemDesign || {};
        const oppDetails = (root as any)?.OpportunityDetails || root;
        const address = (root as any)?.InstallationAddress || (systemDesign as any)?.InstallationAddress;
        const products = (root as any)?.OpportunityProduct || (root as any)?.opportunityProduct || (root as any)?.OpportunityProductsFinanceInfo || [];
        
        // Find product specific finance rows
        const solar = products.find((p: any) => p.Product_Type_Name === 'Solar' || p.product_Type_Name === 'Solar');
        const battery = products.find((p: any) => p.Product_Type_Name === 'Battery' || p.product_Type_Name === 'Battery');
        const pType = globalProductType || (root as any)?.ProductType || (root as any)?.productType || 'B';

        return {
            stateId: address?.stateCode || (root as any)?.SolarUtility?.StateId || '',
            pType: pType === 'BR' ? 'B' : pType,
            roughInspection: oppDetails?.RoughInspection || (root as any)?.RoughInspection || '',
            ahj: oppDetails?.AHJ?.trim() || '',
            solarFinPartner: solar?.Finance_Partner || solar?.finance_Partner || '',
            solarFinMethod: (solar?.Finance_Partner || solar?.finance_Partner) === 'Cash' ? 'Cash Contract' : (solar?.Finance_Method_Name || solar?.finance_Method_Name || ''),
            batteryFinPartner: battery?.Finance_Partner || battery?.finance_Partner || '',
            batteryFinPartnerId: battery?.Finance_Partner_Id || battery?.finance_Partner_Id || '0',
            batteryFinMethod: (battery?.Finance_Partner || battery?.finance_Partner) === 'Cash' ? 'Cash Contract' : (battery?.Finance_Method_Name || battery?.finance_Method_Name || ''),
            designCount: String((root as any)?.DesignArray?.length || '0'),
            oppBatteryMfg: oppDetails?.Battery_Manufacturer_Name || oppDetails?.Battery_Manufacturer || oppDetails?.BatteryManufacturer
        };
    }, [opportunityDetails, systemDesign, globalProductType]);

    // Executes the Excel-based battery/inverter calculation for dynamic field values
    const runBatteryCalculator = useCallback(async (mfg: string, qty: string, outputs: string[]) => {
        const ctx = getExcelContext();
        const payload = {
            ExcelFileName: "Battery Inverter Calculator",
            SheetName: "Inverter Configurator",
            InputValues: [
                { CellNumber: "B3", CellValue: String(ctx.stateId || ''), Type: "String" },
                { CellNumber: "C3", CellValue: String(ctx.pType || 'B'), Type: "String" },
                { CellNumber: "F3", CellValue: String(mfg || '0'), Type: "String" },
                { CellNumber: "G3", CellValue: String(qty || '0'), Type: "String" },
                { CellNumber: "B12", CellValue: String(ctx.roughInspection || ''), Type: "String" },
                { CellNumber: "B9", CellValue: String(ctx.solarFinPartner || ''), Type: "String" },
                { CellNumber: "C9", CellValue: String(ctx.solarFinMethod || ''), Type: "String" },
                { CellNumber: "D9", CellValue: String(ctx.batteryFinPartner || ''), Type: "String" },
                { CellNumber: "E9", CellValue: String(ctx.batteryFinMethod || ''), Type: "String" },
                { CellNumber: "C12", CellValue: String(ctx.ahj || ''), Type: "String" },
                { CellNumber: "G6", CellValue: String(ctx.designCount || '0'), Type: "String" },
            ],
            OutputCells: outputs
        };
        return await masterServices.cascadingDropDown(payload);
    }, [getExcelContext]);

    // Derived Options
    const manufacturers = useMemo(() => {
        if (fetchedOpts.manufacturers) return fetchedOpts.manufacturers;
        const raw = equipmentMasterData?.StorageManufacturers || equipmentMasterData?.BatteryManufacturers || [];
        return raw.map((m: any) => ({ value: m.Value, label: m.Text }));
    }, [equipmentMasterData, fetchedOpts.manufacturers]);

    // Calculates model dropdown options based on selected manufacturer
    const models = useMemo(() => {
        let list = fetchedOpts.models || equipmentMasterData?.StorageModels || equipmentMasterData?.BatteryModels || [];
        const mapped = list.map((m: any) => ({ 
            value: String(m.Value || m.value || ''), 
            label: m.Text || m.label, 
            mfg: m.Manufacturer || m.manufacturer || m.mfg 
        }));
        return selectedMfg ? mapped.filter((m: any) => !m.mfg || m.mfg === selectedMfg) : mapped;
    }, [equipmentMasterData, selectedMfg, fetchedOpts.models]);

    // The function you want the parent to trigger
    const updateBatteryDetails = async () => {
        const sd = systemDesign;

        const vals = await form.validateFields();
        let BatteryInformation = {
            Manufacturer: vals.manufacturer,
            Name: vals.name,
            Quantity: Number(vals.quantity),
            BatterySystemDesignId: Number(
                sd?.BatteryInformation?.BatterySystemDesignId || sd?.BatteryProductId || 0,
            ),
        }

        setSystemDesign({ ...sd, BatteryInformation:BatteryInformation });

    };

    // Expose the function to the parent ref
    useImperativeHandle(ref, () => ({
        updateBatteryDetails: updateBatteryDetails
    }));

    // Hydrates initial state from either passed props or backend design
    useEffect(() => {
        if (!open) return;
        const init = async () => {
            setLoading(prev => ({ ...prev, initial: true }));
            try {
                const ctx = getExcelContext();
                const systemDesignBatteryInfo = (systemDesign as any)?.BatteryInformation;
                const oppFinanceInfo = (systemDesign as any)?.OpportunityProductsFinanceInfo || [];
                const backup = oppFinanceInfo.find((e: any) => e.ActiveFlag && e.Product_Type_Name === 'Battery');

                // Priorities: Modal Props > System Design > Finance Backup
                const srcMfg = (batteryData?.manufacturer && batteryData.manufacturer !== '—') ? batteryData.manufacturer : (systemDesignBatteryInfo?.Manufacturer || null);
                const srcName = (batteryData?.rawName && batteryData.rawName !== '—') ? batteryData.rawName : (systemDesignBatteryInfo?.Name || null);
                const srcQty = (batteryData?.quantity && batteryData.quantity !== 0 && batteryData.quantity !== '—') ? batteryData.quantity : (systemDesignBatteryInfo?.Quantity || null);
                const teslaFallbackMfg = DEFAULT_BATTERY_MANUFACTURER;
                const initMfg = srcMfg && srcMfg !== '0' ? srcMfg : (ctx.oppBatteryMfg || teslaFallbackMfg || '0');
                const qtyForCalc = srcQty && Number(srcQty) > 0 ? String(srcQty) : String(DEFAULT_BATTERY_QUANTITY);
                const res = await runBatteryCalculator(initMfg, qtyForCalc, ["D6", "F6"]);
                
                let mfgList: any[] = [], qtyList: any[] = [];
                const outputValues = res?.[0]?.OutputValues || res || [];
                (Array.isArray(outputValues) ? outputValues : []).forEach((e: any) => {
                    if (e.CellNumber === 'D6' && e.CellValue) mfgList = String(e.CellValue).split('|').map(v => ({ value: v.trim(), label: v.trim() }));
                    if (e.CellNumber === 'F6' && e.CellValue) qtyList = String(e.CellValue).split('|').map(v => ({ value: v.trim(), label: v.trim() }));
                });

                const teslaFromList = pickBatteryManufacturerValue(mfgList, DEFAULT_BATTERY_MANUFACTURER);
                const finalMfg = mfgList.some(m => m.value === initMfg)
                    ? initMfg
                    : teslaFromList || mfgList[0]?.value || '0';
                let modelList: any[] = [], finalQtyList = qtyList;

                if (finalMfg !== '0') {
                    // Logic for fetching or using cached battery models
                    if (batteryModelData && batteryModelData.length > 0 && (batteryModelData[0]?.Manufacturer === finalMfg || batteryModelData[0]?.mfg === finalMfg)) {
                        modelList = batteryModelData.map((m: any) => ({ value: String(m.Value || m.value || ''), label: m.Text || m.label, mfg: m.Manufacturer || m.mfg }));
                    } else {
                        const mRes: any = await designServices.getBatteryModel(finalMfg, ctx.batteryFinPartnerId);
                        const raw = mRes?.data || mRes || [];
                        modelList = raw.map((m: any) => ({ value: String(m.Value), label: m.Text, mfg: m.Manufacturer }));
                        setBatteryModelData(raw);
                    }

                    // Refetch precise quantities and derived technical attributes (coupling etc)
                    const qRes: any = await runBatteryCalculator(finalMfg, qtyForCalc, ["F6", "G17", "L17", "Q17", "R17"]);
                    const qCells = qRes?.[0]?.OutputValues || qRes || [];
                    const qVal = qCells.find((e: any) => e.CellNumber === 'F6')?.CellValue || '';
                    if (qVal) finalQtyList = qVal.split('|').filter(Boolean).map((v: string) => ({ value: v.trim(), label: v.trim() }));

                    setBatteryExtraInfo({
                        BatteryCoupling: qCells.find((e: any) => e.CellNumber === 'G17')?.CellValue || null,
                        BatteryEquipmentId: qCells.find((e: any) => e.CellNumber === 'L17')?.CellValue || null,
                        BatteryInverterEquipmentId: qCells.find((e: any) => e.CellNumber === 'Q17')?.CellValue || null,
                        BatteryInverterQuantity: qCells.find((e: any) => e.CellNumber === 'R17')?.CellValue || null
                    });
                }

                setFetchedOpts({ manufacturers: mfgList, models: modelList, quantities: finalQtyList });

                const isValidName = srcName !== null && srcName !== undefined && srcName !== 0 && srcName !== '0' && modelList.some((m: any) => String(m.value) === String(srcName) || String(m.label) === String(srcName));
                const defaultName = pickBatteryModelValue(modelList, DEFAULT_BATTERY_MODEL_NAME);
                const finalName = isValidName
                    ? String(modelList.find((m: any) => String(m.value) === String(srcName) || String(m.label) === String(srcName))?.value ?? srcName)
                    : (defaultName || modelList[0]?.value || undefined);
                const finalQty = String(srcQty && Number(srcQty) > 0 ? srcQty : '');
                const defaultQty = pickBatteryQuantityValue(finalQtyList, DEFAULT_BATTERY_QUANTITY);
                const finalSelectedQty = finalQtyList.some(q => q.value === finalQty && finalQty !== '' && finalQty !== '0')
                    ? finalQty
                    : (defaultQty || finalQtyList[0]?.value || undefined);
                
                form.setFieldsValue({ manufacturer: finalMfg !== '0' ? finalMfg : undefined, name: finalName, quantity: finalSelectedQty });
            } catch (e) { console.error("Init Error", e); }
            setLoading(prev => ({ ...prev, initial: false }));
        };
        init();
    }, [open]); // eslint-disable-line

    // Cascade effect for manual manufacturer/model changes
    const handleCascade = async (mfg: string, name: any, isMfgChange: boolean) => {
        if (!mfg || mfg === '0') return;
        setLoading(prev => ({ ...prev, secondary: true }));
        try {
            const ctx = getExcelContext();
            let modelList = fetchedOpts.models || [];
            if (isMfgChange) {
                // Determine whether to use cache or fetch fresh models
                if (batteryModelData && batteryModelData.length > 0 && (batteryModelData[0]?.Manufacturer === mfg || batteryModelData[0]?.mfg === mfg)) {
                    modelList = batteryModelData.map((m: any) => ({ value: String(m.Value || m.value || ''), label: m.Text || m.label, mfg: m.Manufacturer || m.mfg }));
                } else {
                    const mRes: any = await designServices.getBatteryModel(mfg, ctx.batteryFinPartnerId);
                    const raw = mRes?.data || mRes || [];
                    modelList = raw.map((m: any) => ({ value: String(m.Value), label: m.Text, mfg: m.Manufacturer }));
                    setBatteryModelData(raw);
                }
                setFetchedOpts(p => ({ ...p, models: modelList }));
            }

            // Sync quantities based on current manufacturer
            const resin = await runBatteryCalculator(mfg, '0', ["F6"]);
            const outputs = resin?.[0]?.OutputValues || resin || [];
            const qtyCellValue = outputs.find((e: any) => e.CellNumber === 'F6')?.CellValue || '';
            const qtyList = qtyCellValue.split('|').filter(Boolean).map((v: string) => ({ value: v.trim(), label: v.trim() }));
            setFetchedOpts(p => ({ ...p, quantities: qtyList }));

            form.setFieldsValue({
                name: isMfgChange ? (modelList?.[0]?.value || undefined) : String(name),
                quantity: qtyList?.[0]?.value || undefined
            });
        } catch (e) { console.error("Cascade Error", e); }
        setLoading(prev => ({ ...prev, secondary: false }));
    };

    // Extra Info/Coupling Tracker
    useEffect(() => {
        if (!selectedMfg || !selectedQty || selectedQty === 0) return;
        // Fetches derived technical specs (coupling, inverter config) based on selection
        const fetchExtra = async () => {
            const res = await runBatteryCalculator(selectedMfg, String(selectedQty), ["G17", "L17", "Q17", "R17"]);
            const cells = res?.[0]?.OutputValues || res || [];
            const info: any = {};
            cells.forEach((e: any) => {
                if (e.CellNumber === 'G17') info.BatteryCoupling = e.CellValue;
                if (e.CellNumber === 'L17') info.BatteryEquipmentId = e.CellValue;
                if (e.CellNumber === 'Q17') info.BatteryInverterEquipmentId = e.CellValue;
                if (e.CellNumber === 'R17') info.BatteryInverterQuantity = e.CellValue;
            });
            setBatteryExtraInfo(info);
        };
        fetchExtra();
    }, [selectedMfg, selectedQty, runBatteryCalculator]);

    // Automatically binds the correct model name for Tesla configurations based on coupling info
    useEffect(() => {
        if (isTesla && batteryExtraInfo?.BatteryCoupling && models.length > 0) {
            const found = models.find((m: any) => m.label.toLowerCase().includes(String(batteryExtraInfo.BatteryCoupling).toLowerCase()));
            if (found && String(form.getFieldValue('name')) !== String(found.value)) form.setFieldsValue({ name: String(found.value) });
        }
    }, [isTesla, batteryExtraInfo, models, form]);

    const handleSave = async () => {
        if (designInfoAccessView) return;
        setLoading(prev => ({ ...prev, saving: true }));
        try {
            const vals = await form.validateFields();
            const root = opportunityDetails || systemDesign || {};
            const products = (root as any)?.OpportunityProduct || (root as any)?.opportunityProduct || (root as any)?.OpportunityProductsFinanceInfo || [];
            const solarRow = products.find((p: any) => (p.Product_Type_Name === 'Solar' || p.product_Type_Name === 'Solar') && p.ActiveFlag);

            // Fetch the most current system design state
            const currentDesign = systemDesign || {};
            const batteryProduct = products.find((ele:any) => ele.Product_Type_Name == 'Battery')
            const lightReachData = changeOrderDetails?.find(
                (x: any) =>
                    String(x.Finance_Partner_Name ?? '')
                        .toLocaleLowerCase()
                        .trim() === COMMON_CONSTANTS.LightreachPartnerName.toLocaleLowerCase(),
            );

            const prevQty = Number(currentDesign.BatteryInformation?.Quantity);
            const nextQty = Number(vals.quantity);

            const haveBatteryLightreach: boolean = !!(lightReachData?.Product_Name as string)?.includes('Battery');

            const needsLightreachCoConfirm =
                lightReachData && lightReachData.Application_Id && haveBatteryLightreach &&
                !Number.isNaN(nextQty) &&
                batteryProduct.Finance_Partner_Display_Name.toLocaleLowerCase() == COMMON_CONSTANTS.LightreachPartnerName.toLocaleLowerCase()
                prevQty !== nextQty;


            if (needsLightreachCoConfirm ) {
                pendingBatteryFormValsRef.current = vals;
                awaitingBatteryCoConfirmRef.current = true;
                setBatteryChangeOrderConfirmOpen(true);
                return;
            }

            await persistBatteryDesign(vals);
        } catch (e:any) {
            console.log('Save Error', e?.response?.data?.Message);
            noticeAPI.error({
                message: 'Error',
                description: e?.response?.data?.Message ?? 'Failed to save Battery Information. Please try again.',
                placement: 'topRight',
                className: `obd-notification obd-notification-error obd-theme-${theme}`,
            });
        } finally {
            if (!awaitingBatteryCoConfirmRef.current) {
                setLoading((prev) => ({ ...prev, saving: false }));
            }
        }
    };


    /** Same sequence as `ProductionsCard` `finishForceSyncConfirm(true)` when user chooses to sync before save. */
    const performLightreachForceSyncPreSave = useCallback(async (numericId: string): Promise<boolean> => {
        const opProducts = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const haveLightreachProduct = Array.isArray(opProducts)
            ? opProducts.some((product: any) => product.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName)
            : false;

        if (haveLightreachProduct) {
            try {
                await designServices.lightReachVoidQuote(numericId, opportunityDetails?.Finance_Partner_Sync_LeadID);
            } catch (e: unknown) {
                const err = e as {
                    response?: { data?: { title?: string; Message?: string; errors?: Record<string, unknown> } };
                    message?: string;
                };
                const data = err?.response?.data;
                let text =
                    String(data?.title ?? data?.Message ?? err?.message ?? '').trim() || 'Could not void Lightreach quote.';
                if (data?.errors && typeof data.errors === 'object') {
                    const parts = Object.values(data.errors).flat();
                    if (parts.length > 0) {
                        text = Array.isArray(parts[0]) ? String((parts[0] as string[])[0]) : String(parts[0]);
                    }
                }
                message.error(text);
                return false;
            }
        }

        try {
            await quoteServices.contractUpdateLoanStatus(numericId, UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD, 0);
        } catch (e: unknown) {
            const ax = e as { response?: { data?: { title?: string; Message?: string; errors?: unknown } }; message?: string };
            let text =
                String(ax?.response?.data?.title ?? ax?.response?.data?.Message ?? ax?.message ?? '').trim() ||
                'Could not post system design override to Contract.';
            const data = ax?.response?.data as { errors?: Record<string, unknown> } | undefined;
            if (data?.errors && typeof data.errors === 'object') {
                const parts = Object.values(data.errors).flat();
                if (parts.length > 0) text = Array.isArray(parts[0]) ? String((parts[0] as string[])[0]) : String(parts[0]);
            }
            message.error(text);
            return false;
        }

        try {
            await quoteServices.savePageChanges({
                opportunityId: numericId,
                pageName: 'Design',
                changes:
                    'Force sync: user confirmed saving battery quantity with an active Lightreach change order (Battery Details modal).',
            });
            await quoteServices.getPageChanges(numericId);
        } catch {
            /* best-effort, parity with ProductionsCard */
        }

        try {
            await quoteServices.contractGetLoanStatusByOpportunity(numericId);
        } catch {
            /* Angular continues */
        }
        setQuotesRefreshTrigger(Date.now());
        return true;
    }, [opportunityDetails, setQuotesRefreshTrigger]);

    const buildBatterySavePayload = useCallback(
        (vals: { manufacturer: string; name: string; quantity: string | number }) => {
            const sd = systemDesign;
            const root = opportunityDetails || sd || {};
            const products =
                (root as any)?.OpportunityProduct ||
                (root as any)?.opportunityProduct ||
                (root as any)?.OpportunityProductsFinanceInfo ||
                [];
            const solarRow = products.find(
                (p: any) => (p.Product_Type_Name === 'Solar' || p.product_Type_Name === 'Solar') && p.ActiveFlag,
            );
            return {
                Opportunity_Id: String(opportunityDetails?.Opportunity_Id || sd?.Opportunity_Id),
                OpportunityInterest: opportunityDetails?.OpportunityInterest || sd?.OpportunityInterest,
                SalesForce_Id: opportunityDetails?.SalesForce_Id || sd?.SalesForce_Id,
                SolarProductId: Number(sd?.SolarProductId || 0),
                BatteryProductId: Number(sd?.BatteryProductId || 0),
                BatteryOnlyProductId: Number(sd?.BatteryOnlyProductId || 0),
                RoofProductId: Number(sd?.RoofProductId || 0),
                RoofOnlyProductId: Number(sd?.RoofOnlyProductId || 0),
                SolarUtility: sd?.SolarUtility,
                BatteryInformation: {
                    Manufacturer: vals.manufacturer,
                    Name: Number(vals.name),
                    Quantity: Number(vals.quantity),
                    BatterySystemDesignId: Number(
                        sd?.BatteryInformation?.BatterySystemDesignId || sd?.BatteryProductId || 0,
                    ),
                },
                Roof: sd?.Roof,
                RoofOnly: sd?.RoofOnly,
                BatteryOnly: sd?.BatteryOnly,
                Multiplier: sd?.Multiplier ?? null,
                SystemSize: sd?.SystemSize ?? null,
                SystemSizeDouble: Number(sd?.SystemSizeDouble || 0),
                Production: sd?.Production ?? null,
                ProductionDouble: Number(sd?.ProductionDouble || 0),
                IsDomestic: sd?.IsDomestic ?? false,
                FinanceMethod: solarRow?.Finance_Method_Name || sd?.FinanceMethod || '',
                designJson: sd?.designJson || (sd?.DesignArray ? JSON.stringify(sd.DesignArray) : '[]'),
                isManual: sd?.isManual ?? false,
            } as Record<string, unknown>;
        },
        [opportunityDetails, systemDesign],
    );

    const persistBatteryDesign = useCallback(
        async (vals: { manufacturer: string; name: string; quantity: string | number }) => {
            const payload = buildBatterySavePayload(vals);
            await designServices.SaveSystemDesign(payload);



            const refresh = await designServices.RetrieveSystemDesign(String(payload.Opportunity_Id ?? ''));
            if (refresh) {
                setSystemDesign(refresh);

                let roofData = refresh?.Roof as any;
                if(roofData){
                    const roof = roofData?.RoofSquareOverride;
                    const normal = roofData?.Normal_Slope_Override;
                    const low = roofData?.Low_Slope_Override;
                    const prev = useOneButtonStore.getState().priceQuoteOverrides;
                    setPriceQuoteOverrides({
                        ...prev,
                        roofSquareOverride: typeof roof === 'number' && Number.isFinite(roof) ? roof : (prev?.roofSquareOverride ?? null),
                        normalSlopeOverride: typeof normal === 'number' && Number.isFinite(normal) ? normal : (prev?.normalSlopeOverride ?? null),
                        lowSlopeOverride: typeof low === 'number' && Number.isFinite(low) ? low : (prev?.lowSlopeOverride ?? null),
                    });
                }
            }
            noticeAPI.success({
                message: 'Success',
                description: 'Battery details updated successfully.',
                placement: 'topRight',
                className: `obd-notification obd-notification-success obd-theme-${theme}`,
            });

            const priceQuote = await quoteServices.getRetrievePriceQuote(String(payload.Opportunity_Id ?? ''));
            setPriceQuoteDetails(priceQuote as any);
            setTimeout(onClose, 800);
        },
        [buildBatterySavePayload, noticeAPI, onClose, setSystemDesign, theme],
    );

    // Helper to map long product names/types to the short codes used by the finance partner APIs.
    const getNormalizationCode = (type: string) => {
        const t = (type || '').toLowerCase();
        if (t.includes(SolarProductName.toLowerCase())) return 'S';
        if (t.includes(RoofProductName.toLowerCase())) return 'R';
        if (t.includes(BatteryProductName.toLowerCase())) return 'B';
        if (t.includes(RRRProductName.toLowerCase())) return 'RRR';
        return 'S'; // Default to Solar if unknown
    };

    const finishBatteryChangeOrderConfirm = useCallback(
        async (syncWithLightreach: boolean) => {
            setBatteryChangeOrderConfirmOpen(false);
            const vals = pendingBatteryFormValsRef.current;
            pendingBatteryFormValsRef.current = null;
            awaitingBatteryCoConfirmRef.current = false;

            if (!vals) return;

            setLoading((prev) => ({ ...prev, saving: true }));
            try {
                const oppId = String(opportunityDetails?.Opportunity_Id || systemDesign?.Opportunity_Id || '').trim();
                if (syncWithLightreach && oppId) {
                    const ok = await performLightreachForceSyncPreSave(oppId);
                    if (!ok) return;
                } else {
                    try {
                        await quoteServices.contractUpdateLoanStatus(oppId, UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD, 0);
                    } catch (e: unknown) {
                        const ax = e as { response?: { data?: { title?: string; Message?: string; errors?: unknown } }; message?: string };
                        let text =
                            String(ax?.response?.data?.title ?? ax?.response?.data?.Message ?? ax?.message ?? '').trim() ||
                            'Could not post system design override to Contract.';
                        const data = ax?.response?.data as { errors?: Record<string, unknown> } | undefined;
                        if (data?.errors && typeof data.errors === 'object') {
                            const parts = Object.values(data.errors).flat();
                            if (parts.length > 0) text = Array.isArray(parts[0]) ? String((parts[0] as string[])[0]) : String(parts[0]);
                        }
                        message.error(text);
                        return false;
                    }
            
                    try {
                        await quoteServices.savePageChanges({
                            opportunityId: oppId,
                            pageName: 'Design',
                            changes:
                                'Force sync: user confirmed saving battery quantity with an active Lightreach change order (Battery Details modal).',
                        });
                        await quoteServices.getPageChanges(oppId);
                    } catch {
                        /* best-effort, parity with ProductionsCard */
                    }
            
                    try {
                        await quoteServices.contractGetLoanStatusByOpportunity(oppId);
                    } catch {
                        /* Angular continues */
                    }
                    setQuotesRefreshTrigger(Date.now());
                }
                await persistBatteryDesign(vals);
            } catch (e:any) {
                console.log('Save Error', e);
                noticeAPI.error({
                    message: 'Error',
                    description:  e?.response?.data?.Message ?? 'Failed to save Battery Information. Please try again.',
                    placement: 'topRight',
                    className: `obd-notification obd-notification-error obd-theme-${theme}`,
                });
            } finally {
                setLoading((prev) => ({ ...prev, saving: false }));
            }
        },
        [
            noticeAPI,
            opportunityDetails?.Opportunity_Id,
            performLightreachForceSyncPreSave,
            persistBatteryDesign,
            systemDesign?.Opportunity_Id,
            theme,
        ],
    );

    const isBusy = loading.initial || loading.secondary || loading.saving;

    const batteryModalFooter = (
        <div className="obd-battery-modal__actions">
            <Button icon={<X size={14} weight="bold" />} onClick={onClose} disabled={isBusy}>
                Cancel
            </Button>
            <Button
                type="primary"
                icon={<FloppyDisk size={14} weight="bold" />}
                onClick={handleSave}
                disabled={isBusy || designInfoAccessView}
                loading={loading.saving}
            >
                Save
            </Button>
        </div>
    );

    useProductDesignsEmbeddedFooter(
        embedded && !embedFooterInline,
        embeddedActive,
        open,
        batteryModalFooter,
        onEmbeddedFooterChange,
    );

    const batteryModalBody = (
        <>
            {noticeContext}
            <div className="obd-battery-modal__inner" style={{ position: 'relative' }}>
                <div className="obd-battery-modal__fields">
                    <Form form={form} layout="vertical" className="obd-form" requiredMark={false}>
                        <Form.Item name="manufacturer" label={<span>Manufacturer <span className="text-danger">*</span></span>} rules={[{ required: true, message: 'Required' }]}>
                            <OBDSelect showSearch disabled={isBusy || designInfoAccessView} options={manufacturers} onChange={(v) => handleCascade(String(v ?? ''), form.getFieldValue('name'), true)}
                                filterOption={(i, o) => (o?.label ?? '').toString().toLowerCase().includes(String(i).toLowerCase())} popupClassName={`obd-select-dropdown obd-theme-${theme}`} />
                        </Form.Item>
                        <Form.Item name="name" label={<span>Name <span className="text-danger">*</span></span>} rules={[{ required: true, message: 'Required' }]}>
                            <OBDSelect showSearch disabled={isBusy || designInfoAccessView || isTesla} options={models} onChange={(v) => handleCascade(String(selectedMfg ?? ''), v, false)}
                                filterOption={(i, o) => (o?.label ?? '').toString().toLowerCase().includes(String(i).toLowerCase())} popupClassName={`obd-select-dropdown obd-theme-${theme}`} />
                        </Form.Item>
                        <Form.Item name="quantity" label={<span>Quantity <span className="text-danger">*</span></span>} rules={[{ required: true, message: 'Required' }]}>
                            <OBDSelect disabled={isBusy || designInfoAccessView} options={fetchedOpts.quantities || []} popupClassName={`obd-select-dropdown obd-theme-${theme}`} />
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </>
    );

    const changeOrderConfirmModal = (
        <Modal
            title="Confirm system design refresh"
            open={batteryChangeOrderConfirmOpen}
            zIndex={13200}
            centered
            maskClosable
            okText="OK"
            cancelText="Cancel"
            rootClassName={`obd-theme-${theme} obd-productions-force-sync-confirm`}
            onOk={() => void finishBatteryChangeOrderConfirm(true)}
            onCancel={() => void finishBatteryChangeOrderConfirm(false)}
            afterClose={() => {
                if (awaitingBatteryCoConfirmRef.current) {
                    awaitingBatteryCoConfirmRef.current = false;
                    pendingBatteryFormValsRef.current = null;
                    setLoading((prev) => ({ ...prev, saving: false }));
                }
            }}
        >
            <div>
                <div>
                    <b>Notice</b>: Battery Qty has been changed. This will require a customer re-sign.
                </div>
                <ul>
                    <li>
                        To sync the Design / Battery Qty changes with Lightreach, click <b>OK</b>.
                    </li>
                    <li>
                        To save the Design / Battery Qty changes without syncing, click <b>Cancel</b>.
                    </li>
                </ul>
                <div>
                    <b>Important</b>: If you click Cancel but plan to create a new quote, you must return to this
                    design, save it, and sync it with the Lightreach portal by clicking <b>“OK”</b> before
                    generating the quote. This ensures the quote reflects the correct system size and accurate
                    financial details.
                </div>
            </div>
        </Modal>
    );

    if (embedded) {
        return (
            <>
                <div
                    className="obd-battery-details-embedded obd-battery-modal obd-battery-modal--inline-fields"
                    style={{ position: 'relative' }}
                >
                    {batteryModalBody}
                    {embedFooterInline ? batteryModalFooter : null}
                    {isBusy && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10,
                                background: theme === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.4)',
                                backdropFilter: 'blur(1px)',
                                zIndex: 1000,
                                borderRadius: 16,
                            }}
                        >
                            <Spin />
                            <span style={{ fontWeight: 500, color: theme === 'dark' ? 'rgba(248, 250, 252, 0.98)' : '#1e293b' }}>
                                Loading details…
                            </span>
                        </div>
                    )}
                </div>
                {changeOrderConfirmModal}
            </>
        );
    }

    return (
        <>
            <DashboardModal
                open={open}
                onClose={() => !isBusy && !batteryChangeOrderConfirmOpen && onClose()}
                width={440}
                centered
                title="Battery Details"
                maskClosable={false}
                theme={theme}
                destroyOnClose
                className={`obd-battery-modal obd-theme-${theme}`}
                rootClassName={`obd-battery-modal-root obd-theme-${theme}`}
                loading={isBusy}
                loadingMessage="Loading details..."
                footer={batteryModalFooter}
            >
                {batteryModalBody}
            </DashboardModal>
            {changeOrderConfirmModal}
        </>
    );
};

export default BatteryDetailsModal;
